import { ReactNode } from 'react';
import BackToHomeLink from './BackToHomeLink';

type MaxWidth = 'prose' | '3xl' | '4xl' | '5xl';

type StaticPageShellProps = {
    title: string;
    eyebrow?: string;
    subtitle?: string;
    children: ReactNode;
    showBack?: boolean;
    maxWidth?: MaxWidth;
};

const widthClass: Record<MaxWidth, string> = {
    prose: 'max-w-prose',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
};

/**
 * Shared layout for public info/legal pages (About, FAQ, Shipping, Privacy, TOS, Rules).
 * Hero + content stack use SiteSettings CSS vars only.
 */
const StaticPageShell = ({
    title,
    eyebrow,
    subtitle,
    children,
    showBack = true,
    maxWidth = '3xl',
}: StaticPageShellProps) => {
    return (
        <div className={`mx-auto w-full ${widthClass[maxWidth]} px-1 py-6 sm:py-10`}>
            {showBack && (
                <div className="mb-3 sm:mb-4">
                    <BackToHomeLink />
                </div>
            )}

            <header className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--accent-color)] to-[color-mix(in_srgb,var(--text-color),transparent_55%)] px-5 py-8 text-center shadow-lg sm:mb-8 sm:rounded-3xl sm:px-10 sm:py-12">
                {eyebrow ? (
                    <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-[var(--text-color)]/85 sm:text-sm">
                        {eyebrow}
                    </p>
                ) : null}
                <h1 className="text-2xl font-bold leading-tight text-[var(--text-color)] sm:text-4xl">
                    {title}
                </h1>
                {subtitle ? (
                    <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-[var(--text-color)]/90 sm:text-base">
                        {subtitle}
                    </p>
                ) : null}
                <div
                    className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10"
                    aria-hidden
                />
                <div
                    className="pointer-events-none absolute -bottom-12 -left-8 h-48 w-48 rounded-full bg-white/10"
                    aria-hidden
                />
            </header>

            <div className="space-y-4 sm:space-y-5">{children}</div>
        </div>
    );
};

type StaticSectionProps = {
    title: string;
    children: ReactNode;
    icon?: string;
    id?: string;
};

/** Card section for static/legal content — rounded border, accent tint, mobile-friendly padding. */
export const StaticSection = ({ title, children, icon, id }: StaticSectionProps) => {
    return (
        <section
            id={id}
            className="rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_45%)] bg-[color-mix(in_srgb,var(--bg-color),white_35%)] p-5 shadow-sm sm:p-6"
        >
            <h2 className="mb-2.5 flex items-start gap-2.5 text-lg font-bold text-[var(--text-color)] sm:text-xl">
                {icon ? (
                    <span className="mt-0.5 shrink-0 text-xl leading-none" aria-hidden>
                        {icon}
                    </span>
                ) : null}
                <span>{title}</span>
            </h2>
            <div className="space-y-3 text-sm leading-relaxed text-[var(--text-color)]/90 sm:text-base [&_a]:font-medium [&_a]:text-[var(--accent-color)] [&_a]:underline [&_a]:underline-offset-2 [&_h3]:mt-4 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-[var(--text-color)] sm:[&_h3]:text-lg [&_li]:leading-relaxed [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-5">
                {children}
            </div>
        </section>
    );
};

/** Soft CTA / footer note card for static pages. */
export const StaticCtaCard = ({ children }: { children: ReactNode }) => {
    return (
        <div className="rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)] p-5 text-center shadow-sm sm:p-7">
            <div className="text-sm leading-relaxed text-[var(--text-color)] sm:text-base [&_a]:font-semibold [&_a]:text-[var(--accent-color)] [&_a]:underline [&_a]:underline-offset-2">
                {children}
            </div>
        </div>
    );
};

export default StaticPageShell;
